import { useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { SecondScreenOperation } from "@/Common/Models/SecondScreen/Enum/SecondScreenOperation";
import { SecondScreenNoteService } from "@/Common/Services/SecondScreenNoteService";
import { NotesConfigurationModel } from "@/Patient/Models/NotesConfigurationModel";
import { NotesConfigurationSettings } from "@/Patient/Models/Settings/NotesConfigurationSettings";
import { NotesFilterSettings } from "@/Patient/Models/Settings/NotesFilterSettings";
import { MedicalNotesConfigurationActions } from "@/Patient/Stores/Actions/MedicalNotesConfigurationActions";
import { MedicalNotesConfigurationPopup } from "./MedicalNotesConfigurationPopup";

interface IMedicalNotesConfigurationProps {
    className?: string;
    onConfigurationChange?: (dateFrom?: Date, dateTo?: Date) => void
}

function MedicalNotesConfiguration(props: IMedicalNotesConfigurationProps): JSX.Element {
    const { getText } = useTranslation();
    const [showNotesConfigurationPopup, setShowNotesConfigurationPopup] = useState<boolean>(false);

    function onCloseNotesConfigurationPopup(): void {
        setShowNotesConfigurationPopup(false);
    }

    function onOpenNotesConfigurationPopup(): void {
        setShowNotesConfigurationPopup(true);
    }

    async function saveNotesConfiguration(configs: NotesConfigurationModel, patient: string = "default", dateFrom: Date, dateTo: Date): Promise<void> {
        const notesFilterSettings = cacheStore.UserSettings?.NotesFilterSettings ?? new NotesFilterSettings();

        if (patient === "default" || !notesFilterSettings.NotesConfigurationSettings) {
            notesFilterSettings.NotesConfigurationSettings = new NotesConfigurationSettings();
        }
        notesFilterSettings.NotesConfigurationSettings[patient] = { ...configs };
        await MedicalNotesConfigurationActions.save(notesFilterSettings, sessionStore.userId);
        props.onConfigurationChange(dateFrom, dateTo);
        patientStore.EmitChangeToListeners();
        SecondScreenNoteService.getInstance().triggerSecondScreenUpdate(SecondScreenOperation.ConfigureNotes, { dateFrom, dateTo });
        onCloseNotesConfigurationPopup();
    }

    return (
        <div className={props.className}>
            <div className="margin-xs-top margin-sm-left align-left">
                <a
                    onClick={() => onOpenNotesConfigurationPopup()}
                    data-cy="medicalNote-notes-configuration-button"
                >
                    {getText("ConfigureNotes_ButtonLabel")}
                </a>
            </div>
            {showNotesConfigurationPopup && (
                <MedicalNotesConfigurationPopup
                    onCancel={() => onCloseNotesConfigurationPopup()}
                    onApplyToAllPatients={(configs, dateFrom, dateTo) => saveNotesConfiguration(configs, "default", dateFrom, dateTo)}
                    onApplyToCurrentPatient={(configs, patientId, dateFrom, dateTo) => saveNotesConfiguration(configs, patientId, dateFrom, dateTo)}
                />
            )}
        </div>
    );
}

export default MedicalNotesConfiguration;
